import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertTriangle, Loader2, Wallet, FileText, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PaymentsTab({ language }) {
  const [sessionId, setSessionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/wallet/recover-stripe-session`, {
        session_id: sessionId.trim(),
      });
      setResult(data);
      setSessionId('');
    } catch (err) {
      setError(formatApiError(err, language === 'lv' ? 'Kļūda' : 'Error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="admin-payments-tab">
      <div className="flex items-start gap-3 mb-4">
        <Wallet className="w-6 h-6 text-[#D97736] mt-0.5" />
        <div>
          <h2 className="font-outfit text-xl font-bold text-[#121212]">
            {language === 'lv' ? 'Maksājumu atveseļošana' : 'Payment Recovery'}
          </h2>
          <p className="text-sm text-[#5C5C5C] mt-1 max-w-2xl">
            {language === 'lv'
              ? 'Ja lietotājs Stripe maksājumu pabeidza, bet maciņš netika papildināts (webhook neizpildījās vai logs tika aizvērts), ievadi Stripe sesijas ID un ieskaitīsim summu manuāli. Operācija ir idempotenta — ja jau ieskaitīts, neieskaitīs vēlreiz.'
              : 'If a Stripe payment succeeded but the wallet was not credited (webhook failed or window closed), enter the Stripe session ID to credit it manually. The operation is idempotent — already credited sessions stay safe.'}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3 max-w-xl">
        <div>
          <label className="block text-xs text-[#5C5C5C] mb-1">Stripe Session ID</label>
          <Input
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="cs_live_..."
            required
            className="font-mono text-sm"
            data-testid="admin-recover-session-input"
          />
          <p className="text-xs text-[#8A8A8A] mt-1">
            {language === 'lv'
              ? 'Atrodi Stripe Dashboard → Payments vai lietotāja e-pasta kvītī.'
              : 'Find it in the Stripe Dashboard → Payments or in the user receipt email.'}
          </p>
        </div>
        <Button
          type="submit"
          disabled={busy || !sessionId.trim()}
          className="bg-[#D97736] hover:bg-[#C06528] text-white"
          data-testid="admin-recover-session-btn"
        >
          {busy
            ? (language === 'lv' ? 'Pārbauda...' : 'Checking...')
            : (language === 'lv' ? 'Atveseļot un ieskaitīt' : 'Recover & credit')}
        </Button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm max-w-xl" data-testid="admin-recover-error">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl max-w-xl" data-testid="admin-recover-success">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
            <CheckCircle className="w-5 h-5" />
            {result.already_credited
              ? (language === 'lv' ? 'Jau ieskaitīts iepriekš' : 'Already credited previously')
              : (language === 'lv' ? 'Ieskaitīts veiksmīgi' : 'Credited successfully')}
          </div>
          <dl className="text-sm space-y-1 text-[#121212]">
            <div className="flex gap-2"><dt className="text-[#5C5C5C] min-w-[100px]">{language === 'lv' ? 'Lietotājs' : 'User'}:</dt><dd>{result.user_name} <span className="text-[#5C5C5C]">({result.user_email})</span></dd></div>
            <div className="flex gap-2"><dt className="text-[#5C5C5C] min-w-[100px]">{language === 'lv' ? 'Summa' : 'Amount'}:</dt><dd>€{result.amount?.toFixed(2)}</dd></div>
            <div className="flex gap-2"><dt className="text-[#5C5C5C] min-w-[100px]">Session:</dt><dd className="font-mono text-xs break-all">{result.session_id}</dd></div>
          </dl>
        </div>
      )}

      <PaymentEventsLog language={language} />
      <FailedPayoutsLog language={language} />
    </div>
  );
}

function FailedPayoutsLog({ language }) {
  const isLV = language === 'lv';
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/withdrawals`, { params: { status: 'rejected' } });
      // Only Stripe Connect failures are retryable (IBAN/manual ones are handled differently).
      setRows((res.data || []).filter((w) => w.method === 'stripe_connect'));
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const retry = async (id) => {
    setRetrying(id);
    try {
      const res = await axios.post(`${API}/admin/withdrawals/${id}/retry-payout`);
      toast.success(res.data?.message || (isLV ? 'Izmaksa atkārtota!' : 'Payout retried!'));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isLV ? 'Atkārtošana neizdevās' : 'Retry failed'));
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#E5E4E0]" data-testid="admin-failed-payouts">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <div>
            <h3 className="font-outfit text-lg font-bold text-[#121212]">
              {isLV ? 'Neizdevušās Stripe izmaksas' : 'Failed Stripe payouts'}
            </h3>
            <p className="text-sm text-[#5C5C5C]">
              {isLV
                ? 'Atkārto izmaksu pēc tam, kad pārdevējs salabojis bankas kontu. Nauda jau atgriezta maciņā.'
                : 'Retry a payout after the seller fixes their bank account. Funds were already refunded to the wallet.'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8 w-8 p-0" data-testid="admin-failed-payouts-refresh">
          <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading && !rows ? (
        <div className="py-6 text-center text-[#5C5C5C] text-sm">{isLV ? 'Ielādē...' : 'Loading...'}</div>
      ) : (rows && rows.length ? (
        <div className="space-y-3">
          {rows.map((w) => (
            <div key={w.id} className="flex items-start justify-between gap-3 flex-wrap border border-red-100 bg-red-50/40 rounded-xl p-3" data-testid={`admin-failed-payout-${w.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#121212]">{w.user_name || '—'}</span>
                  <span className="text-xs text-[#5C5C5C]">{w.user_email}</span>
                  <span className="text-sm font-bold text-[#121212]">€{Number(w.amount || 0).toFixed(2)}</span>
                  {w.retry_count > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      {isLV ? `mēģinājumi: ${w.retry_count}` : `retries: ${w.retry_count}`}
                    </span>
                  )}
                </div>
                {w.error && (
                  <div className="text-xs text-red-600 mt-1 break-words" title={w.error}>
                    {isLV ? 'Kļūda: ' : 'Error: '}{String(w.error).slice(0, 180)}
                  </div>
                )}
                <div className="text-[10px] text-[#5C5C5C] mt-1">
                  {w.created_at ? new Date(w.created_at).toLocaleString(isLV ? 'lv-LV' : 'en-US') : ''}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => retry(w.id)}
                disabled={retrying === w.id}
                className="h-8 bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid={`admin-retry-payout-${w.id}`}
              >
                {retrying === w.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><RotateCw className="w-3.5 h-3.5 mr-1.5" />{isLV ? 'Atkārtot izmaksu' : 'Retry payout'}</>}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 text-center text-[#5C5C5C] text-sm flex items-center justify-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
          {isLV ? 'Nav neizdevušos izmaksu' : 'No failed payouts'}
        </div>
      ))}
    </div>
  );
}

function PaymentEventsLog({ language }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payments/recent`, {
        params: { limit: 50, type: typeFilter || undefined, status: statusFilter || undefined },
      });
      setData(res.data);
    } catch (err) {
      // soft fail — log section is non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, statusFilter]);

  const typeLabel = {
    order: isLV ? 'Pasūtījums' : 'Order',
    wallet_topup: isLV ? 'Maciņa papildināšana' : 'Wallet top-up',
    premium_subscription: 'Premium',
  };
  const statusColor = {
    paid: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    unpaid: 'bg-red-100 text-red-700',
    failed: 'bg-red-100 text-red-700',
    expired: 'bg-gray-100 text-gray-600',
  };
  const statusLabels = {
    paid: isLV ? 'Apmaksāts' : 'Paid',
    pending: isLV ? 'Gaida' : 'Pending',
    unpaid: isLV ? 'Neapmaksāts' : 'Unpaid',
    failed: isLV ? 'Neizdevies' : 'Failed',
    expired: isLV ? 'Beidzies' : 'Expired',
  };
  // Status pills derive from whatever statuses actually exist in the data
  // (summary) so new statuses like 'unpaid'/'failed' appear automatically.
  const statusOptions = ['', ...Object.keys(data?.summary || {}).filter((k) => k && k !== 'unknown')];
  const pill = (active) =>
    `text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active ? 'bg-[#D97736] text-white border-[#D97736]'
             : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'
    }`;

  return (
    <div className="mt-6 pt-6 border-t border-[#E5E4E0]" data-testid="admin-payment-events">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-[#D97736]" />
          <div>
            <h3 className="font-outfit text-lg font-bold text-[#121212]">
              {isLV ? 'Stripe maksājumu žurnāls' : 'Stripe payment log'}
            </h3>
            <p className="text-sm text-[#5C5C5C]">
              {isLV ? 'Pēdējie 50 maksājumu notikumi (webhook redzamība)' : 'Last 50 payment events (webhook visibility)'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost" size="sm" onClick={load} disabled={loading}
          className="h-8 w-8 p-0" data-testid="admin-payment-events-refresh"
        >
          <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {data?.summary && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-payment-summary">
          {Object.entries(data.summary).map(([k, v]) => (
            <span key={k} className={`text-xs px-2.5 py-1 rounded-full ${statusColor[k] || 'bg-gray-100 text-gray-600'}`}>
              {k}: {v}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[['', isLV ? 'Visi tipi' : 'All types'], ['order', typeLabel.order], ['wallet_topup', typeLabel.wallet_topup], ['premium_subscription', 'Premium']].map(([val, lab]) => (
          <button key={val || 'all'} onClick={() => setTypeFilter(val)} className={pill(typeFilter === val)} data-testid={`admin-pay-type-${val || 'all'}`}>{lab}</button>
        ))}
        <span className="w-px h-5 bg-[#E5E4E0] mx-1" />
        {statusOptions.map((val) => (
          <button key={val || 'allstatus'} onClick={() => setStatusFilter(val)} className={pill(statusFilter === val)} data-testid={`admin-pay-status-${val || 'all'}`}>
            {val ? (statusLabels[val] || val) : (isLV ? 'Visi statusi' : 'All statuses')}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border border-[#E5E4E0] rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E4E0] bg-[#FAFAFA] text-xs text-[#5C5C5C] uppercase">
              <th className="text-left px-3 py-2">{isLV ? 'Datums' : 'Date'}</th>
              <th className="text-left px-3 py-2">{isLV ? 'Tips' : 'Type'}</th>
              <th className="text-left px-3 py-2">{isLV ? 'Lietotājs' : 'User'}</th>
              <th className="text-right px-3 py-2">{isLV ? 'Summa' : 'Amount'}</th>
              <th className="text-left px-3 py-2">{isLV ? 'Statuss' : 'Status'}</th>
              <th className="text-left px-3 py-2">Session</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5C5C5C]">{isLV ? 'Ielādē...' : 'Loading...'}</td></tr>
            ) : (data?.transactions?.length ? data.transactions.map((t) => (
              <tr key={t.id} className="border-b border-[#E5E4E0] last:border-0" data-testid={`admin-pay-row-${t.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-[#5C5C5C]">{t.created_at ? new Date(t.created_at).toLocaleString(isLV ? 'lv-LV' : 'en-US') : '—'}</td>
                <td className="px-3 py-2">{typeLabel[t.type] || t.type}</td>
                <td className="px-3 py-2">
                  {t.user_name
                    ? <span>{t.user_name}<br /><span className="text-[10px] text-[#5C5C5C]">{t.user_email}</span></span>
                    : <span className="text-[#5C5C5C]">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-medium text-[#121212]">€{(t.amount || 0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[t.payment_status] || 'bg-gray-100 text-gray-600'}`}>{t.payment_status}</span>
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-[#5C5C5C] max-w-[160px] truncate" title={t.session_id}>{t.session_id}</td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5C5C5C]">{isLV ? 'Nav notikumu' : 'No events'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PaymentsTab;
